import { calendrier, fiche, kanban, liste, tableau } from '@taki/ui/views/core';
import type { Column, ScreenFilterDef, ViewBinding, ViewType } from '@taki/ui';
import { jauge } from './views/jauge.js';
import {
  STATUS_OPTIONS,
  TEAM_OPTIONS,
  labelOf,
  ownerInitials,
  setDemoStatus,
  type DemoIntervention,
} from './demo-data.js';

/**
 * CE QUE L'ECRAN DECLARE : les six vues proposees, et un BINDING par vue.
 *
 * C'est le coeur de la demonstration. L'ecran ne dit jamais COMMENT dessiner,
 * il dit CE QUE SONT ses donnees : quel champ metier joue le role de titre, de
 * statut, de regroupement, de date. Le core projette, la vue rend.
 *
 * UN BINDING PAR VUE, et pas un binding unique : la meme donnee ne se raconte
 * pas pareil en liste et en kanban. Le kanban est la seule vue a declarer
 * `set()` sur `groupKey`, donc la seule ou une carte est glissable : sans
 * `set()`, aucune affordance, jamais une carte qui revient toute seule dans sa
 * colonne.
 *
 * Ces objets vivent au niveau du MODULE (jamais recrees a chaque rendu) : leurs
 * closures sont stables, la projection ne se recalcule que quand les donnees ou
 * la requete changent.
 */

/**
 * L'ordre du selecteur (zone 4). Les cinq vues du core viennent du sous-chemin
 * `@taki/ui/views/core`, importees EXPLICITEMENT : sans cet import, le build
 * les supprimerait. La sixieme, `jauge`, est locale a l'app et n'a aucun
 * privilege en moins. Le selecteur groupe tout seul : collection, filet, fiche.
 */
export const DEMO_VIEW_TYPES: ViewType[] = [liste, tableau, kanban, calendrier, jauge, fiche];

/** Ids passes a `useScreen` et a `ViewDefaultSetting` : ils doivent coincider. */
export const DEMO_APP_ID = 'ui-lab';
export const DEMO_SCREEN_ID = 'vues';

/**
 * Les colonnes : `value` sert au tri, a l'export et a l'agregat ; `render` ne
 * sert QU'A l'affichage (la duree se lit "3 h", mais se trie et se moyenne sur
 * le nombre).
 */
const DEMO_COLUMNS: Column<DemoIntervention>[] = [
  { key: 'owner', label: 'Assigne a', value: (item) => item.owner, sortable: true },
  {
    key: 'team',
    label: 'Equipe',
    // Le tri se fait sur le MOT AFFICHE : c'est la seule regle qu'un
    // utilisateur peut deviner en cliquant sur un en-tete.
    value: (item) => labelOf(TEAM_OPTIONS, item.team),
    sortable: true,
  },
  { key: 'start', label: 'Debut', value: (item) => item.startAt, sortable: true },
  {
    key: 'duration',
    label: 'Duree',
    value: (item) => item.duration,
    render: (item) => `${item.duration} h`,
    sortable: true,
    agg: 'avg',
  },
  { key: 'cost', label: 'Cout (EUR)', value: (item) => item.cost, sortable: true, agg: 'sum' },
];

export const DEMO_BINDINGS: ViewBinding<DemoIntervention>[] = [
  {
    /* LISTE : reconnaitre. Quatre roles suffisent. */
    type: 'core.liste',
    roles: {
      title: { get: (item) => item.subject },
      // `empty` : le texte affiche quand la valeur est vide (une intervention
      // du decor n'a pas de lieu), jamais une chaine vide ni "undefined".
      subtitle: { get: (item) => item.place, empty: 'Lieu non renseigne' },
      // Le role `avatar` porte une URL OU des initiales : c'est l'ECRAN qui
      // tranche, la vue ne devine rien.
      avatar: { get: (item) => ownerInitials(item.owner) },
      status: { get: (item) => item.status, options: () => STATUS_OPTIONS },
    },
  },
  {
    /* TABLEAU : comparer. Sans colonne declaree, l'entree serait grisee. */
    type: 'core.tableau',
    roles: {
      title: { get: (item) => item.subject },
      avatar: { get: (item) => ownerInitials(item.owner) },
      status: { get: (item) => item.status, options: () => STATUS_OPTIONS },
    },
    columns: DEMO_COLUMNS,
    // Sac libre, lu par ce SEUL type de vue : le titre n'est pas un nom ici.
    options: { titleLabel: 'Intervention', statusLabel: 'Etat' },
  },
  {
    /* KANBAN : faire avancer. `options()` est obligatoire (sinon colonnes
       fantomes), `set()` est ce qui rend la carte glissable. */
    type: 'core.kanban',
    roles: {
      title: { get: (item) => item.subject },
      subtitle: { get: (item) => item.place, empty: 'Lieu non renseigne' },
      avatar: { get: (item) => ownerInitials(item.owner) },
      groupKey: {
        get: (item) => item.status,
        options: () => STATUS_OPTIONS,
        set: (item, value) => setDemoStatus(item.id, String(value)),
      },
    },
  },
  {
    /* CALENDRIER : placer dans le temps. `dateEnd` etale l'evenement sur tous
       les jours couverts ; `lane` en fait une puce de couloir. */
    type: 'core.calendrier',
    roles: {
      title: { get: (item) => item.subject },
      subtitle: { get: (item) => item.place, empty: 'Lieu non renseigne' },
      status: { get: (item) => item.status, options: () => STATUS_OPTIONS },
      dateStart: { get: (item) => item.startAt },
      dateEnd: { get: (item) => item.endAt ?? null },
      lane: { get: (item) => item.team, options: () => TEAM_OPTIONS },
    },
  },
  {
    /* VUE CUSTOM : le role `avancement` n'existe pas dans le core. L'ecran le
       declare comme n'importe quel autre role, il atterrit dans `row.extra`. */
    type: 'ui-lab.jauge',
    roles: {
      title: { get: (item) => item.subject },
      subtitle: { get: (item) => item.place, empty: 'Lieu non renseigne' },
      avancement: { get: (item) => item.progress },
    },
  },
  {
    /* FICHE : consulter. Elle affiche les roles restants puis les colonnes,
       d'ou un binding riche : c'est la vue la plus detaillee. */
    type: 'core.fiche',
    roles: {
      title: { get: (item) => item.subject },
      subtitle: { get: (item) => item.place, empty: 'Lieu non renseigne' },
      avatar: { get: (item) => ownerInitials(item.owner) },
      status: { get: (item) => item.status, options: () => STATUS_OPTIONS },
      groupKey: { get: (item) => item.status, options: () => STATUS_OPTIONS },
      lane: { get: (item) => item.team, options: () => TEAM_OPTIONS },
      dateStart: { get: (item) => item.startAt },
      dateEnd: { get: (item) => item.endAt ?? null },
    },
    columns: DEMO_COLUMNS,
  },
];

/**
 * Les pastilles de la zone 4. Le `match` est ce qui permet au CORE de filtrer :
 * sans lui, la pastille serait purement decorative. Le filtre s'applique AVANT
 * la projection, donc il vaut pour les six vues d'un coup.
 */
export const DEMO_FILTERS: ScreenFilterDef<DemoIntervention>[] = [
  { id: 'toutes', label: 'Toutes' },
  { id: 'ouvertes', label: 'Ouvertes', match: (item) => item.status !== 'termine' },
  { id: 'terminees', label: 'Terminees', match: (item) => item.status === 'termine' },
];
